import React, { useState } from 'react';

const Footer = () => {
  const [hovered, setHovered] = useState(null);

  const colors = {
    navy: '#07111f',
    navy2: '#0b1b2d',
    navy3: '#10263d',

    white: '#ffffff',
    softWhite: '#e8f1f8',
    muted: '#91a6b9',

    blue: '#38bdf8',
    blueDark: '#0284c7',
    blueSoft: '#7dd3fc',

    line: 'rgba(148,163,184,0.13)',
    glow: 'rgba(56,189,248,0.15)',
  };

  const styles = {
    section: {
      position: 'relative',
      width: '100%',
      minHeight: '100vh',
      background: `
        radial-gradient(
          circle at 15% 20%,
          rgba(56,189,248,0.10),
          transparent 25%
        ),
        radial-gradient(
          circle at 85% 75%,
          rgba(2,132,199,0.10),
          transparent 28%
        ),
        linear-gradient(
          135deg,
          ${colors.navy},
          ${colors.navy2}
        )
      `,
      color: colors.white,
      overflow: 'hidden',
      fontFamily: '"Inter", system-ui, -apple-system, sans-serif',
      boxSizing: 'border-box',
      display: 'flex',
      alignItems: 'center',
    },

    grid: {
      position: 'absolute',
      inset: 0,
      backgroundImage: `
        linear-gradient(
          rgba(148,163,184,0.045) 1px,
          transparent 1px
        ),
        linear-gradient(
          90deg,
          rgba(148,163,184,0.045) 1px,
          transparent 1px
        )
      `,
      backgroundSize: '55px 55px',
      animation: 'engineeringGrid 35s linear infinite',
      pointerEvents: 'none',
    },

    diagonalLine: {
      position: 'absolute',
      width: '120%',
      height: '1px',
      background:
        'linear-gradient(90deg, transparent, rgba(56,189,248,0.35), transparent)',
      transform: 'rotate(-24deg)',
      top: '22%',
      left: '-10%',
      animation: 'lineMove 7s ease-in-out infinite',
      pointerEvents: 'none',
    },

    diagonalLineTwo: {
      position: 'absolute',
      width: '100%',
      height: '1px',
      background:
        'linear-gradient(90deg, transparent, rgba(125,211,252,0.18), transparent)',
      transform: 'rotate(24deg)',
      bottom: '22%',
      right: '-10%',
      animation: 'lineMoveReverse 9s ease-in-out infinite',
      pointerEvents: 'none',
    },

    container: {
      position: 'relative',
      zIndex: 5,
      width: '100%',
      maxWidth: '1350px',
      margin: '0 auto',
      padding: '6rem 5% 2rem',
      boxSizing: 'border-box',
    },

    topLabel: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.6rem',
      padding: '0.45rem 0.85rem',
      borderRadius: '999px',
      background: 'rgba(56,189,248,0.07)',
      border: '1px solid rgba(56,189,248,0.18)',
      color: colors.blueSoft,
      fontSize: '0.72rem',
      fontWeight: '800',
      letterSpacing: '0.15em',
      textTransform: 'uppercase',
      marginBottom: '1.5rem',
    },

    pulse: {
      width: '7px',
      height: '7px',
      borderRadius: '50%',
      backgroundColor: colors.blue,
      boxShadow: `0 0 15px ${colors.blue}`,
      animation: 'pulseDot 2s infinite',
    },

    heroGrid: {
      display: 'grid',
      gridTemplateColumns: '1.35fr 0.65fr',
      gap: '5rem',
      alignItems: 'center',
    },

    heading: {
      fontSize: 'clamp(3.3rem, 7vw, 7rem)',
      lineHeight: '0.95',
      letterSpacing: '-0.065em',
      fontWeight: '850',
      margin: 0,
      color: colors.white,
      maxWidth: '900px',
    },

    headingHighlight: {
      display: 'block',
      color: colors.blue,
      position: 'relative',
    },

    headingUnderline: {
      position: 'absolute',
      left: 0,
      bottom: '-8px',
      width: '55%',
      height: '4px',
      borderRadius: '10px',
      background:
        `linear-gradient(90deg, ${colors.blue}, transparent)`,
      animation: 'underlineGrow 3s ease-in-out infinite',
    },

    intro: {
      maxWidth: '680px',
      color: colors.muted,
      fontSize: '1.05rem',
      lineHeight: '1.75',
      margin: '2rem 0 0',
    },

    buttonRow: {
      display: 'flex',
      gap: '1rem',
      flexWrap: 'wrap',
      marginTop: '2rem',
    },

    primaryButton: (isHovered) => ({
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.7rem',
      padding: '1rem 1.4rem',
      borderRadius: '10px',
      textDecoration: 'none',
      color: '#ffffff',
      fontWeight: '750',
      fontSize: '0.9rem',
      background:
        `linear-gradient(135deg, ${colors.blue}, ${colors.blueDark})`,
      boxShadow: isHovered
        ? '0 18px 45px rgba(56,189,248,0.28)'
        : '0 10px 30px rgba(56,189,248,0.12)',
      transform: isHovered
        ? 'translateY(-4px)'
        : 'translateY(0)',
      transition: 'all 0.3s ease',
    }),

    secondaryButton: (isHovered) => ({
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.7rem',
      padding: '1rem 1.4rem',
      borderRadius: '10px',
      textDecoration: 'none',
      color: isHovered ? colors.blue : colors.softWhite,
      fontWeight: '650',
      fontSize: '0.9rem',
      border: `1px solid ${isHovered
          ? 'rgba(56,189,248,0.45)'
          : 'rgba(148,163,184,0.18)'
        }`,
      background: 'rgba(255,255,255,0.025)',
      transform: isHovered
        ? 'translateY(-4px)'
        : 'translateY(0)',
      transition: 'all 0.3s ease',
    }),

    // --------------------------------
    // RIGHT ENGINEERING PANEL
    // --------------------------------

    panel: {
      position: 'relative',
      minHeight: '390px',
      borderRadius: '24px',
      background: `
        linear-gradient(
          145deg,
          rgba(255,255,255,0.065),
          rgba(255,255,255,0.018)
        )
      `,
      border: '1px solid rgba(148,163,184,0.16)',
      backdropFilter: 'blur(14px)',
      boxShadow: '0 30px 80px rgba(0,0,0,0.25)',
      padding: '2rem',
      overflow: 'hidden',
    },

    panelTop: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '2rem',
    },

    panelTitle: {
      fontSize: '0.72rem',
      textTransform: 'uppercase',
      letterSpacing: '0.15em',
      color: colors.blueSoft,
      fontWeight: '800',
    },

    panelNumber: {
      fontSize: '0.7rem',
      color: colors.muted,
    },

    blueprint: {
      position: 'relative',
      height: '200px',
      border: `1px solid ${colors.line}`,
      backgroundImage: `
        linear-gradient(
          rgba(56,189,248,0.08) 1px,
          transparent 1px
        ),
        linear-gradient(
          90deg,
          rgba(56,189,248,0.08) 1px,
          transparent 1px
        )
      `,
      backgroundSize: '25px 25px',
      overflow: 'hidden',
    },

    beamHorizontal: {
      position: 'absolute',
      left: '12%',
      right: '12%',
      top: '50%',
      height: '8px',
      background:
        `linear-gradient(90deg, transparent, ${colors.blue}, transparent)`,
      boxShadow: `0 0 18px rgba(56,189,248,0.35)`,
    },

    beamVerticalOne: {
      position: 'absolute',
      left: '25%',
      top: '15%',
      bottom: '15%',
      width: '6px',
      background: colors.blueDark,
      opacity: 0.8,
    },

    beamVerticalTwo: {
      position: 'absolute',
      right: '25%',
      top: '15%',
      bottom: '15%',
      width: '6px',
      background: colors.blueDark,
      opacity: 0.8,
    },

    diagonalBeam: {
      position: 'absolute',
      width: '75%',
      height: '4px',
      background: colors.blueSoft,
      top: '50%',
      left: '12%',
      transform: 'rotate(-22deg)',
      boxShadow: `0 0 12px rgba(125,211,252,0.4)`,
      animation: 'beamFloat 4s ease-in-out infinite',
    },

    panelStats: {
      display: 'grid',
      gridTemplateColumns: 'repeat(3, 1fr)',
      gap: '0.75rem',
      marginTop: '1rem',
    },

    stat: {
      padding: '0.9rem',
      background: 'rgba(255,255,255,0.035)',
      border: `1px solid ${colors.line}`,
      borderRadius: '10px',
      textAlign: 'center',
    },

    statNumber: {
      display: 'block',
      color: colors.blue,
      fontWeight: '800',
      fontSize: '1.1rem',
    },

    statLabel: {
      display: 'block',
      color: colors.muted,
      fontSize: '0.65rem',
      marginTop: '0.25rem',
    },

    // --------------------------------
    // LOWER INFORMATION
    // --------------------------------

    infoSection: {
      marginTop: '5rem',
      paddingTop: '2rem',
      borderTop: `1px solid ${colors.line}`,
      display: 'grid',
      gridTemplateColumns: '1.1fr 1fr 1fr',
      gap: '3rem',
    },

    infoTitle: {
      color: colors.white,
      fontSize: '0.75rem',
      fontWeight: '800',
      textTransform: 'uppercase',
      letterSpacing: '0.13em',
      marginBottom: '1rem',
    },

    infoText: {
      color: colors.muted,
      fontSize: '0.85rem',
      lineHeight: '1.7',
      margin: 0,
    },

    links: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.65rem',
    },

    link: (isHovered) => ({
      color: isHovered ? colors.blue : colors.muted,
      textDecoration: 'none',
      fontSize: '0.85rem',
      transition: 'all 0.25s ease',
      transform: isHovered
        ? 'translateX(5px)'
        : 'translateX(0)',
    }),

    socialRow: {
      display: 'flex',
      gap: '0.6rem',
      marginTop: '0.8rem',
      flexWrap: 'wrap',
    },

    social: (isHovered) => ({
      width: '38px',
      height: '38px',
      borderRadius: '9px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      textDecoration: 'none',
      color: isHovered ? colors.blue : colors.muted,
      border: `1px solid ${isHovered
          ? 'rgba(56,189,248,0.4)'
          : colors.line
        }`,
      background: isHovered
        ? 'rgba(56,189,248,0.08)'
        : 'rgba(255,255,255,0.02)',
      transform: isHovered
        ? 'translateY(-4px)'
        : 'translateY(0)',
      transition: 'all 0.3s ease',
      fontWeight: '800',
      fontSize: '0.8rem',
    }),

    copyright: {
      marginTop: '3rem',
      paddingTop: '1.3rem',
      borderTop: `1px solid ${colors.line}`,
      display: 'flex',
      justifyContent: 'space-between',
      gap: '1rem',
      flexWrap: 'wrap',
      color: '#65788b',
      fontSize: '0.72rem',
    },
  };

  const navLinks = [
    ['Home', '#home'],
    ['About Us', '#About'],
    ['What We Do', '#capabilities'],
    ['Services', '#Services'],
    ['Projects', '#projects'],
    ['Contact', '#Contact'],
  ];

  const socials = [
    ['in', 'https://www.linkedin.com/'],
    ['f', 'https://www.facebook.com/'],
    ['X', 'https://x.com/'],
    ['◎', 'https://www.instagram.com/'],
  ];

  return (
    <section id="contact-footer" style={styles.section}>

      <style>{`

        @keyframes engineeringGrid {
          from {
            background-position: 0 0;
          }

          to {
            background-position: 55px 55px;
          }
        }

        @keyframes lineMove {
          0%, 100% {
            opacity: 0.15;
            transform: rotate(-24deg) translateX(-40px);
          }

          50% {
            opacity: 0.7;
            transform: rotate(-24deg) translateX(40px);
          }
        }

        @keyframes lineMoveReverse {
          0%, 100% {
            opacity: 0.1;
            transform: rotate(24deg) translateX(40px);
          }

          50% {
            opacity: 0.5;
            transform: rotate(24deg) translateX(-40px);
          }
        }

        @keyframes pulseDot {
          0%, 100% {
            opacity: 1;
            transform: scale(1);
          }

          50% {
            opacity: 0.4;
            transform: scale(0.7);
          }
        }

        @keyframes underlineGrow {
          0%, 100% {
            width: 35%;
            opacity: 0.6;
          }

          50% {
            width: 65%;
            opacity: 1;
          }
        }

        @keyframes beamFloat {
          0%, 100% {
            transform: rotate(-22deg) translateX(-5px);
          }

          50% {
            transform: rotate(-22deg) translateX(5px);
          }
        }

        .footer-main-link:hover {
          color: #38bdf8 !important;
        }

        .footer-email-link:hover {
          color: #38bdf8 !important;
        }

        @media (max-width: 1000px) {

          .footer-hero-grid {
            grid-template-columns: 1fr !important;
            gap: 3rem !important;
          }

          .footer-engineering-panel {
            max-width: 650px;
          }

          .footer-info-grid {
            grid-template-columns: 1fr 1fr !important;
          }

        }

        @media (max-width: 650px) {

          .footer-container-main {
            padding: 5rem 6% 1.5rem !important;
          }

          .footer-heading-main {
            font-size: 3.2rem !important;
          }

          .footer-intro-main {
            font-size: 0.95rem !important;
          }

          .footer-button-row {
            flex-direction: column !important;
          }

          .footer-primary-button,
          .footer-secondary-button {
            width: 100% !important;
            justify-content: center !important;
          }

          .footer-engineering-panel {
            min-height: 350px !important;
          }

          .footer-info-grid {
            grid-template-columns: 1fr !important;
            gap: 2rem !important;
            margin-top: 3.5rem !important;
          }

          .footer-stats {
            grid-template-columns: 1fr 1fr 1fr !important;
          }

          .footer-copyright {
            flex-direction: column !important;
          }

        }

        @media (max-width: 400px) {

          .footer-heading-main {
            font-size: 2.7rem !important;
          }

          .footer-panel {
            padding: 1.3rem !important;
          }

        }

        @media (prefers-reduced-motion: reduce) {
          *,
          *::before,
          *::after {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
          }
        }

      `}</style>

      <div style={styles.grid} />
      <div style={styles.diagonalLine} />
      <div style={styles.diagonalLineTwo} />

      <div
        className="footer-container-main"
        style={styles.container}
      >

        {/* ========================= */}
        {/* HERO */}
        {/* ========================= */}

        <div className="footer-hero-grid" style={styles.heroGrid}>

          <div>

            <div style={styles.topLabel}>
              <span style={styles.pulse} />
              SANSA CONSULTANT India LLP
            </div>

            <h2
              className="footer-heading-main"
              style={styles.heading}
            >
              Let's Build
              <span
                style={styles.headingHighlight}
              >
                Something Strong.
                <span style={styles.headingUnderline} />
              </span>
            </h2>

            <p
              className="footer-intro-main"
              style={styles.intro}
            >
              Engineering ideas into reality — from structural analysis and Tekla modelling to land development and civil drafting, we create accurate, practical solutions built for real-world execution across India.
            </p>

            <div
              className="footer-button-row"
              style={styles.buttonRow}
            >

              <a
                href="#contact"
                className="footer-primary-button"
                style={styles.primaryButton(
                  hovered === 'primary'
                )}
                onMouseEnter={() => setHovered('primary')}
                onMouseLeave={() => setHovered(null)}
              >
                Start a Project
                <span>→</span>
              </a>

              <a
                href="mailto:info@sansaconsultant.com"
                className="footer-secondary-button"
                style={styles.secondaryButton(
                  hovered === 'email'
                )}
                onMouseEnter={() => setHovered('email')}
                onMouseLeave={() => setHovered(null)}
              >
                Email Our Team
                <span>↗</span>
              </a>

            </div>

          </div>

          {/* ========================= */}
          {/* ENGINEERING VISUAL */}
          {/* ========================= */}

          <div
            className="footer-engineering-panel footer-panel"
            style={styles.panel}
          >

            <div style={styles.panelTop}>

              <span style={styles.panelTitle}>
                Structural Concept
              </span>

              <span style={styles.panelNumber}>
                SANSA / {new Date().getFullYear()}
              </span>

            </div>

            <div style={styles.blueprint}>

              <div style={styles.beamHorizontal} />

              <div style={styles.beamVerticalOne} />

              <div style={styles.beamVerticalTwo} />

              <div style={styles.diagonalBeam} />

            </div>

            <div
              className="footer-stats"
              style={styles.panelStats}
            >

              <div style={styles.stat}>
                <span style={styles.statNumber}>
                  01
                </span>

                <span style={styles.statLabel}>
                  ANALYZE
                </span>
              </div>

              <div style={styles.stat}>
                <span style={styles.statNumber}>
                  02
                </span>

                <span style={styles.statLabel}>
                  DESIGN
                </span>
              </div>

              <div style={styles.stat}>
                <span style={styles.statNumber}>
                  03
                </span>

                <span style={styles.statLabel}>
                  DELIVER
                </span>
              </div>

            </div>

          </div>

        </div>

        {/* ========================= */}
        {/* INFORMATION */}
        {/* ========================= */}

        <div
          className="footer-info-grid"
          style={styles.infoSection}
        >

          {/* COMPANY */}

          <div>

            <h4 style={styles.infoTitle}>
              Engineering Partner
            </h4>

            <p style={styles.infoText}>
              SANSA CONSULTANT INDIA LLP
              <br />
              Pune, Maharashtra, India
              <br />
              Structural Engineering • Design • Consultancy
            </p>

          </div>

          {/* NAVIGATION */}

          <div>

            <h4 style={styles.infoTitle}>
              Explore
            </h4>

            <div style={styles.links}>

              {navLinks.map((item, index) => (

                <a
                  key={index}
                  href={item[1]}
                  className="footer-main-link"
                  style={styles.link(
                    hovered === `nav-${index}`
                  )}
                  onMouseEnter={() =>
                    setHovered(`nav-${index}`)
                  }
                  onMouseLeave={() =>
                    setHovered(null)
                  }
                >
                  {item[0]}
                </a>

              ))}

            </div>

          </div>

          {/* CONTACT */}

          <div>

            <h4 style={styles.infoTitle}>
              Connect
            </h4>

            <p style={styles.infoText}>
              Let's discuss your next engineering
              requirement.
            </p>

            <a
              href="mailto:info@sansaconsultant.com"
              className="footer-email-link"
              style={{
                display: 'inline-block',
                color: colors.blueSoft,
                textDecoration: 'none',
                marginTop: '0.7rem',
                fontSize: '0.85rem',
                transition: 'color 0.25s ease',
              }}
            >
              info@sansaconsultant.com
            </a>

            <div style={styles.socialRow}>

              {socials.map((social, index) => (

                <a
                  key={index}
                  href={social[1]}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social[0]}
                  style={styles.social(
                    hovered === `social-${index}`
                  )}
                  onMouseEnter={() =>
                    setHovered(`social-${index}`)
                  }
                  onMouseLeave={() =>
                    setHovered(null)
                  }
                >
                  {social[0]}
                </a>

              ))}

            </div>

          </div>

        </div>

        {/* ========================= */}
        {/* COPYRIGHT */}
        {/* ========================= */}

        <div
          className="footer-copyright"
          style={styles.copyright}
        >

          <span>
            © {new Date().getFullYear()} SANSA CONSULTANT
            India LLP. All rights reserved.
          </span>

          <span>
            Engineering with precision. Built for tomorrow.
          </span>

        </div>

      </div>

    </section>
  );
};

export default Footer;